import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Card from '@material-ui/core/Card'
import Box from '@material-ui/core/Box'
import CardContent from '@material-ui/core/CardContent'
import CardMedia from '@material-ui/core/CardMedia'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import IconButton from '@material-ui/core/IconButton'
import Chip from '@material-ui/core/Chip'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import MoreVertIcon from 'components/material-symbol/icons/MoreVert'
import LockOutlinedIcon from 'components/material-symbol/icons/Lock'
import VisibilityOffOutlinedIcon from 'components/material-symbol/icons/VisibilityOff'
import LocationOnOutlinedIcon from 'components/material-symbol/icons/LocationOn'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import ScheduleOutlinedIcon from 'components/material-symbol/icons/Schedule'
import PeopleOutlineIcon from 'components/material-symbol/icons/PeopleOutline'
import pluralize from 'lib/pluralize'
import {
  formattedGroupEventPrice,
  isRecurringPayment,
} from 'utils/groupEventUtils'
import { isRegistrationNotYetOpen } from 'pages/things-to-do/events/register/registration-cta-utils'
import { composeOptimizedImageUrl } from 'lib/utils'
import { interactiveCardHoverStyles } from 'lib/styles/interactive-card-hover'

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    '&.MuiPaper-rounded': {
      borderRadius: '12px',
    },
    '&.MuiPaper-root': {
      boxShadow: 'none',
    },
    ...interactiveCardHoverStyles(theme),
  },
  mediaWrapper: {
    position: 'relative',
    cursor: 'pointer',
  },
  media: {
    height: 140,
  },
  mediaBadges: {
    position: 'absolute',
    left: theme.spacing(1.5),
    bottom: theme.spacing(1.5),
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chipOverlay: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
  },
  iconBadge: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg, & .MuiSvgIcon-root': {
      width: 14,
      height: 14,
      fontSize: 14,
    },
  },
  chipAccessCode: {
    backgroundColor: '#F1E9D8',
    color: '#5C4922',
  },
  chipPrivate: {
    backgroundColor: '#CCDDFF',
    color: '#001C57',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 !important',
    '&.MuiCardContent-root:last-child': {
      paddingBottom: '0 !important',
    },
  },
  cardInner: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1.5),
    paddingLeft: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
  },
  clickableContent: {
    cursor: 'pointer',
  },
  cardTitle: {
    marginBottom: theme.spacing(2),
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    overflow: 'clip',
    textOverflow: 'ellipsis',
    lineHeight: 1.3,
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  metadataRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  metadataItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    maxWidth: '100%',
  },
  metadataIcon: {
    color: theme.palette.text.secondary,
    width: 14,
    height: 14,
    fontSize: 14,
    flexShrink: 0,
    opacity: 0.85,
  },
  metadataText: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
  },
  price: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
    margin: 0,
  },
  footerPrice: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    flex: '1 1 auto',
    gap: theme.spacing(0.5, 1),
    minWidth: 0,
  },
  canceledChip: {
    backgroundColor: '#FFBCBC',
    color: '#410002',
    borderRadius: 8,
    height: 20,
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
    '& .MuiChip-label': {
      color: 'inherit',
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  registrationClosedChip: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.primary,
    borderRadius: 8,
    height: 20,
    fontSize: '0.625rem',
    fontWeight: 500,
    letterSpacing: '0.02em',
    '& .MuiChip-label': {
      color: 'inherit',
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(-1),
    '& .MuiIconButton-root': {
      padding: 0,
      width: 24,
      height: 24,
    },
    '& .MuiIconButton-root .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
}))

const IconBadge = ({ label, className, children }) => (
  <Tooltip title={label}>
    <Box
      component="span"
      className={clsx(className)}
      aria-label={label}
      role="img"
    >
      {children}
    </Box>
  </Tooltip>
)

const getAttendeeLabel = event => {
  const enrolled = event.totalEnrolled ?? event.registrationCount ?? 0

  if (event.maxAttendance) {
    return `${enrolled}/${event.maxAttendance} ${pluralize(
      event.maxAttendance,
      'attendee',
    )}`
  }

  return `${enrolled} ${pluralize(enrolled, 'attendee')}`
}

const getDisplayPrice = event => {
  if (event.priceInCents === 0 && !isRecurringPayment(event)) {
    return 'Free'
  }

  return formattedGroupEventPrice(event)
}

const getDateTimeText = event =>
  !event.isPast() ? event.closestEventDayAndTime : event.eventDayAndTime

const splitDateTimeText = dateTimeText => {
  const parts = dateTimeText?.split(' • ') ?? []

  if (parts.length >= 3) {
    return {
      dateLabel: `${parts[0]} • ${parts[1]}`,
      timeLabel: parts.slice(2).join(' • '),
    }
  }

  return { dateLabel: dateTimeText, timeLabel: null }
}

const getDateLabel = event =>
  splitDateTimeText(getDateTimeText(event)).dateLabel

const getTimeAndSessionsLabel = event => {
  const { timeLabel } = splitDateTimeText(getDateTimeText(event))
  const sessionCount = event.sessions?.length ?? 0
  const sessionLabel = `${sessionCount} ${pluralize(sessionCount, 'session')}`

  return timeLabel ? `${timeLabel} · ${sessionLabel}` : sessionLabel
}

const EventCard = ({
  event,
  setIsDetailsOpen,
  cardActions,
  chip,
  actionMenu,
}) => {
  const classes = useStyles()
  const [actionModalAnchor, setActionModalAnchor] = useState(null)

  const handleOpenActionModal = e => {
    e.stopPropagation()
    setActionModalAnchor(e.currentTarget)
  }

  const handleCloseActionModal = () => {
    setActionModalAnchor(null)
  }

  const showAttendeeCount = !event.externalRegistrationUrl
  const waitlistCount = event.waitlist?.entries?.length ?? 0
  const showRegistrationClosed =
    Boolean(event.registrationClosed) &&
    !event.isCanceled &&
    !event.isPast() &&
    !isRegistrationNotYetOpen(event)

  return (
    <Card elevation={0} className={classes.root}>
      <Box
        className={classes.mediaWrapper}
        onClick={() => setIsDetailsOpen('detail')}
      >
        <CardMedia
          className={classes.media}
          image={composeOptimizedImageUrl(event.featuredImageUrl, 960)}
          title={event.title}
        />
        <Box className={classes.mediaBadges}>
          {event.requiresAccessCode ? (
            <IconBadge
              label="Access Code"
              className={clsx(classes.iconBadge, classes.chipAccessCode)}
            >
              <LockOutlinedIcon />
            </IconBadge>
          ) : null}
          {event.unlisted ? (
            <IconBadge
              label="This event is unlisted."
              className={clsx(classes.iconBadge, classes.chipPrivate)}
            >
              <VisibilityOffOutlinedIcon />
            </IconBadge>
          ) : null}
        </Box>
        {chip ? <Box className={classes.chipOverlay}>{chip}</Box> : null}
      </Box>

      <CardContent className={classes.cardContent}>
        <Box className={classes.cardInner}>
          <Box
            className={classes.clickableContent}
            onClick={() => setIsDetailsOpen('detail')}
          >
            <Typography
              component="h3"
              variant="subtitle1"
              className={classes.cardTitle}
            >
              {event.title}
            </Typography>

            <Box className={classes.metadataRow}>
              <Box className={classes.metadataItem}>
                <LocationOnOutlinedIcon className={classes.metadataIcon} />
                <Typography
                  variant="caption"
                  className={classes.metadataText}
                  noWrap
                  title={event.coachFacility.name}
                >
                  {event.coachFacility.name}
                </Typography>
              </Box>
              <Box className={classes.metadataItem}>
                <CalendarTodayIcon className={classes.metadataIcon} />
                <Typography
                  variant="caption"
                  className={classes.metadataText}
                  noWrap
                  title={getDateLabel(event)}
                >
                  {getDateLabel(event)}
                </Typography>
              </Box>
              <Box className={classes.metadataItem}>
                <ScheduleOutlinedIcon className={classes.metadataIcon} />
                <Typography
                  variant="caption"
                  className={classes.metadataText}
                  noWrap
                  title={getTimeAndSessionsLabel(event)}
                >
                  {getTimeAndSessionsLabel(event)}
                </Typography>
              </Box>
              {showAttendeeCount ? (
                <Box className={classes.metadataItem}>
                  <PeopleOutlineIcon className={classes.metadataIcon} />
                  <Typography
                    variant="caption"
                    className={classes.metadataText}
                  >
                    {getAttendeeLabel(event)}
                  </Typography>
                </Box>
              ) : null}
              {waitlistCount > 0 ? (
                <Box className={classes.metadataItem}>
                  <Typography
                    variant="caption"
                    className={classes.metadataText}
                  >
                    {waitlistCount} Waitlist
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>

          <Box className={classes.cardFooter}>
            <Box className={classes.footerPrice}>
              <Typography component="p" className={classes.price}>
                {getDisplayPrice(event)}
              </Typography>
              {event.isCanceled ? (
                <Chip
                  label="Canceled"
                  size="small"
                  className={classes.canceledChip}
                />
              ) : null}
              {showRegistrationClosed ? (
                <Chip
                  label="Registration Closed"
                  size="small"
                  className={classes.registrationClosedChip}
                />
              ) : null}
            </Box>
            <Box className={classes.footerActions}>
              {cardActions}
              {actionMenu ? (
                <>
                  <IconButton
                    aria-label="More actions"
                    onClick={handleOpenActionModal}
                  >
                    <MoreVertIcon color="primary" />
                  </IconButton>
                  <actionMenu.type
                    {...actionMenu.props}
                    event={event}
                    isOpen={Boolean(actionModalAnchor)}
                    anchorEl={actionModalAnchor}
                    handleClose={handleCloseActionModal}
                  />
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default EventCard

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    featuredImageUrl: PropTypes.string.isRequired,
    startDateTime: PropTypes.object.isRequired,
    endDateTime: PropTypes.object.isRequired,
    registrationCloseDateTime: PropTypes.string,
    registrationOpenDateTime: PropTypes.string,
    registrationClosed: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    timezone: PropTypes.string.isRequired,
    isCanceled: PropTypes.bool.isRequired,
    maxAttendance: PropTypes.number,
    requiresAccessCode: PropTypes.bool.isRequired,
    priceInCents: PropTypes.number.isRequired,
    totalPriceInCents: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    academyRequired: PropTypes.bool.isRequired,
    academy: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
    }),
    assistantCoaches: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      }),
    ),
    registrationCount: PropTypes.number.isRequired,
    totalEnrolled: PropTypes.number,
    description: PropTypes.string.isRequired,
    acceptsOnlinePayments: PropTypes.bool.isRequired,
    paymentRecurrenceSchedule: PropTypes.string.isRequired,
    numberOfPayments: PropTypes.number.isRequired,
    externalRegistrationUrl: PropTypes.string,
    seriesEndDateTime: PropTypes.string.isRequired,
    unlisted: PropTypes.bool.isRequired,
    lessonCount: PropTypes.number.isRequired,
    coachFacility: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    coach: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    sessions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        startDateTime: PropTypes.string.isRequired,
        endDateTime: PropTypes.string.isRequired,
      }),
    ),
    waitlist: PropTypes.shape({
      entries: PropTypes.array,
    }),
  }),
  cardActions: PropTypes.node.isRequired,
  chip: PropTypes.node,
  actionMenu: PropTypes.object,
  setIsDetailsOpen: PropTypes.func.isRequired,
}
